<script lang="ts">
  import { Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-svelte';
  import { login, mfaSetupStart, mfaSetup, mfaVerify, type MFASetupInfo } from '$lib/api/auth';
  import { getErrorMessage } from '$lib/api/client';
  import Footer from '$lib/components/Footer.svelte';
  import LanguagePicker from '$lib/components/LanguagePicker.svelte';
  import { t } from '$lib/i18n';
  import { play } from '$lib/sound';

  const { onAuthenticated }: { onAuthenticated: () => void } = $props();

  type Step = 'password' | 'code' | 'setup';

  let step = $state<Step>('password');
  let email = $state('');
  let password = $state('');
  let showPassword = $state(false);
  let loading = $state(false);
  let error = $state('');

  let mfaToken = $state('');
  let code = $state('');
  let setup = $state<MFASetupInfo | null>(null);

  async function submitPassword(e: SubmitEvent) {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      error = t('login.emailPasswordRequired');
      return;
    }
    loading = true;
    error = '';
    try {
      const result = await login(email.trim(), password);
      if (result.status === 'ok') {
        play('success');
        onAuthenticated();
        return;
      }
      mfaToken = result.mfa_token;
      code = '';
      if (result.status === 'mfa_setup_required') {
        setup = await mfaSetupStart(mfaToken);
        step = 'setup';
      } else {
        step = 'code';
      }
      play('ready');
    } catch (err) {
      play('error');
      error = getErrorMessage(err);
    } finally {
      loading = false;
    }
  }

  async function submitCode(e: SubmitEvent) {
    e.preventDefault();
    if (code.trim().length < 6) {
      error = t('login.mfa.codeRequired');
      return;
    }
    loading = true;
    error = '';
    try {
      await mfaVerify(mfaToken, code.trim());
      play('success');
      onAuthenticated();
    } catch (err) {
      play('error');
      error = getErrorMessage(err);
    } finally {
      loading = false;
    }
  }

  async function submitSetup(e: SubmitEvent) {
    e.preventDefault();
    if (code.trim().length < 6) {
      error = t('login.setup.codeRequired');
      return;
    }
    loading = true;
    error = '';
    try {
      await mfaSetup(mfaToken, code.trim());
      play('success');
      onAuthenticated();
    } catch (err) {
      play('error');
      error = getErrorMessage(err);
    } finally {
      loading = false;
    }
  }
</script>

<div class="auth-fixed-light grid min-h-screen font-body md:grid-cols-[1.05fr_.95fr]">
  <div class="relative hidden flex-col justify-between overflow-hidden bg-grad-hero p-14 text-white md:flex">
    <div
      class="pointer-events-none absolute inset-0"
      style="background:radial-gradient(circle at 18% 12%,rgba(151,215,0,.16) 0%,transparent 42%),radial-gradient(circle at 90% 100%,rgba(34,106,115,.55) 0%,transparent 50%)"
    ></div>
    <div class="relative font-heading text-xl font-bold tracking-tight">Teams</div>
    <div class="relative max-w-md">
      <p class="mb-[18px] text-[13px] font-semibold uppercase tracking-[.12em] text-toyama-lime">
        {t('login.brand.eyebrow')}
      </p>
      <h1 class="mb-[18px] font-heading text-[44px] font-bold leading-[1.08] tracking-tight whitespace-pre-line">
        {t('login.brand.title')}
      </h1>
      <p class="text-[17px] font-light leading-relaxed text-white/75">
        {t('login.brand.subtitle')}
      </p>
    </div>
    <div class="relative text-[13px] font-light text-white/60">{t('login.brand.footer')}</div>
  </div>

  <div class="flex flex-col bg-white">
    <div class="flex items-center justify-between px-6 py-4 bg-navy md:hidden">
      <span class="font-heading text-lg font-bold text-white">Teams</span>
      <LanguagePicker compact onLoginScreen />
    </div>

    <div class="flex flex-1 items-center justify-center px-8 py-12">
      <div class="auth-fade-in w-full max-w-[380px]">
        <div class="mb-5 flex items-start justify-between gap-3">
          <div
            class="inline-flex items-center gap-2 rounded-pill bg-navy px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[.06em] text-white"
          >
            <ShieldCheck class="h-3.5 w-3.5 shrink-0 text-toyama-lime" />
            Teams
          </div>
          <div class="hidden md:block">
            <LanguagePicker onLoginScreen />
          </div>
        </div>

        {#if step === 'password'}
          <h2 class="mb-2 font-heading text-[28px] font-bold tracking-tight text-navy">{t('login.signIn')}</h2>
          <p class="mb-8 text-[15px] font-light leading-relaxed text-dark-gray">
            {t('login.signInHint')}
          </p>

          <form class="space-y-5" onsubmit={submitPassword}>
            <div>
              <label class="auth-field-label" for="email">{t('login.email')}</label>
              <input
                id="email"
                type="email"
                required
                autocomplete="username"
                bind:value={email}
                class="auth-field-input"
                placeholder="you@toyamaworld.com"
              />
            </div>
            <div>
              <label class="auth-field-label" for="password">{t('login.password')}</label>
              <div class="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autocomplete="current-password"
                  bind:value={password}
                  class="auth-field-input pr-10"
                  placeholder={t('login.passwordPlaceholder')}
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-dark-gray"
                  onclick={() => (showPassword = !showPassword)}
                  aria-label={t('login.togglePassword')}
                >
                  {#if showPassword}<EyeOff class="h-5 w-5" />{:else}<Eye class="h-5 w-5" />{/if}
                </button>
              </div>
            </div>

            {#if error}
              <div class="flex gap-2 rounded-lg border border-[#A32D2D]/30 bg-[#A32D2D]/5 px-4 py-3">
                <AlertCircle class="mt-0.5 h-4 w-4 shrink-0 text-[#A32D2D]" />
                <p class="text-[13px] text-[#A32D2D]">{error}</p>
              </div>
            {/if}

            <button type="submit" disabled={loading} class="auth-btn-accent w-full" data-cuelume-press data-cuelume-release>
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </form>
        {:else if step === 'code'}
          <h2 class="mb-2 font-heading text-[28px] font-bold tracking-tight text-navy">{t('login.mfa.title')}</h2>
          <p class="mb-7 text-[15px] font-light leading-relaxed text-dark-gray">
            {t('login.mfa.hint')}
          </p>

          <form class="space-y-5" onsubmit={submitCode}>
            <div>
              <label class="auth-field-label" for="mfa-code">{t('login.mfa.code')}</label>
              <input
                id="mfa-code"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                bind:value={code}
                placeholder="123456"
                class="auth-field-input text-center text-lg font-mono tracking-[.4em]"
              />
            </div>
            {#if error}
              <p class="text-center text-[13px] text-[#A32D2D]">{error}</p>
            {/if}
            <button type="submit" disabled={loading} class="auth-btn-accent w-full" data-cuelume-press data-cuelume-release>
              {loading ? t('login.mfa.verifying') : t('login.mfa.verify')}
            </button>
          </form>
        {:else if step === 'setup' && setup}
          <h2 class="mb-2 font-heading text-[28px] font-bold tracking-tight text-navy">
            {t('login.setup.title')}
          </h2>
          <p class="mb-7 text-[15px] font-light leading-relaxed text-dark-gray">
            {t('login.setup.hint')}
          </p>

          <div class="space-y-5">
            <p class="text-[13px] font-light text-dark-gray">
              {t('login.setup.scanHint')}
            </p>
            {#if setup.qr_data_url}
              <div class="flex justify-center">
                <img
                  src={setup.qr_data_url}
                  alt={t('login.setup.qrAlt')}
                  class="h-44 w-44 rounded-lg border border-toyama-cloudy-gray p-3"
                />
              </div>
            {/if}
            <details class="text-[13px]">
              <summary class="cursor-pointer font-medium text-dark-gray hover:text-navy">
                {t('login.setup.manualSummary')}
              </summary>
              <p class="mt-2 text-dark-gray">{t('login.setup.manualHint')}</p>
              <code class="mt-1 block select-all break-all rounded bg-gray-50 px-3 py-2 font-mono text-xs text-navy">
                {setup.secret}
              </code>
            </details>

            <form class="space-y-4 border-t border-toyama-cloudy-gray pt-4" onsubmit={submitSetup}>
              <div>
                <label class="auth-field-label" for="setup-code">{t('login.setup.codeLabel')}</label>
                <input
                  id="setup-code"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  bind:value={code}
                  placeholder="123456"
                  class="auth-field-input text-center text-lg font-mono tracking-[.4em]"
                />
              </div>
              {#if error}
                <p class="text-center text-[13px] text-[#A32D2D]">{error}</p>
              {/if}
              <button type="submit" disabled={loading} class="auth-btn-accent w-full" data-cuelume-press data-cuelume-release>
                {loading ? t('login.setup.enabling') : t('login.setup.enable')}
              </button>
            </form>
          </div>
        {/if}
      </div>
    </div>
    <Footer />
  </div>
</div>
